/**
 * Download — where a signed-in, entitled user (an onboarded tester, TRE-1044/1047)
 * fetches the current CRAFT-signed Treillage installer. This is the Option B
 * distribution surface (TRE-1713): the binary is served by the entitlement-gated
 * `GET /releases/download`, so the browser can't use a plain `<a href>` (it won't
 * attach the bearer). We fetch the Blob through the API client and save it from an
 * object URL instead.
 *
 * Shown to EVERY signed-in user regardless of admin capability — a tester is often
 * a plain `runner`. The server is the real gate: a not-entitled account gets a 403
 * (rendered as a friendly "not active yet"), an account with no published release a
 * 404 ("check back shortly").
 */

import { useState } from "react";
import { api, ApiError } from "../api/client";
import type { AccountContext } from "../api/types";
import { useAsyncData } from "../lib/useAsyncData";
import { useToast } from "../components/Toast";

/** Human-readable size, e.g. `98.4 MB`. */
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

export function Download({ ctx: _ctx }: { ctx: AccountContext }) {
  const release = useAsyncData(() => api.releaseCurrent(), []);
  const [downloading, setDownloading] = useState(false);
  const toast = useToast();

  async function onDownload(fileName: string) {
    setDownloading(true);
    try {
      const blob = await api.downloadInstaller();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke on the next tick so the click's navigation has already started.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      toast.push(e instanceof ApiError ? e.message : "The download failed. Please try again.", "error");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div>
      <h1>Download Treillage</h1>
      <p className="screen-intro">
        Install the Treillage add-in for Autodesk Revit. Run the installer and sign in with this same
        account inside Revit to activate your seat.
      </p>

      {release.loading && <p className="muted">Checking for the latest installer…</p>}
      {release.error && (
        <div className="banner banner--info" role="status">
          {release.error} <button className="btn--link" onClick={release.reload}>Retry</button>
        </div>
      )}

      {release.value && (
        <div className="card card--download">
          <table className="table">
            <tbody>
              <tr>
                <th>Version</th>
                <td>{release.value.version}</td>
              </tr>
              <tr>
                <th>File</th>
                <td>{release.value.file}</td>
              </tr>
              <tr>
                <th>Size</th>
                <td>{formatBytes(release.value.sizeBytes)}</td>
              </tr>
              {release.value.notes && (
                <tr>
                  <th>Notes</th>
                  <td>{release.value.notes}</td>
                </tr>
              )}
              <tr>
                <th>SHA-256</th>
                <td>
                  <code className="hash">{release.value.sha256}</code>
                </td>
              </tr>
            </tbody>
          </table>

          <button
            className="btn btn--primary"
            disabled={downloading}
            onClick={() => void onDownload(release.value!.file)}
          >
            {downloading ? "Downloading…" : `Download ${release.value.file}`}
          </button>
          <p className="muted download-hint">
            After downloading, you can verify integrity by checking the file's SHA-256 matches the
            value above.
          </p>
        </div>
      )}
    </div>
  );
}
